<template>
  <d-container fluid class="main-content-container px-4">
    <!-- Page Header -->
    <d-row no-gutters class="page-header py-4">
      <d-col col sm="6" class="text-center text-sm-left mb-4 mb-sm-0">
        <span class="text-uppercase page-subtitle">{{ $t('settlements.settlements') }}</span>
        <h3 class="page-title">{{ $t('settlements.invoiceList') }}</h3>
      </d-col>
      <d-col
        v-if="!userIsCustomer"
        col
        sm="6"
        class="text-right text-sm-right d-flex justify-content-end align-items-end">
        <d-button
          v-if="userIsAdmin"
          theme="success"
          @click.prevent="addSettlement">
          {{ $t('settlements.addSettlement') }}
        </d-button>

        <d-button
          theme="primary"
          :disabled="downloadingAllInvoices"
          @click.prevent="downloadAllInvoices"
          class="ml-2">
          <template v-if="!downloadingAllInvoices">
            <template v-if="hasActiveFilters">
              {{ $t('settlements.downloadFilteredInvoices') }}
            </template>
            <template v-else>
              {{ $t('settlements.downloadTodaysInvoices') }}
            </template>
          </template>
          <template v-else>
            <div class="spinner-border spinner-border-sm" role="status"></div>
          </template>
        </d-button>
      </d-col>
    </d-row>

    <d-row
      style="width: 100%;"
      class="d-flex ml-0 mb-3 page-filters">
      <validation-observer
        ref="settlements-filters-form"
        tag="form"
        style="width: 100%;"
        class="d-flex">
        <select-customer
          v-if="!userIsCustomer"
          :customerID="filters.customer_id"
          style="width: 40%;" />

        <date-range-picker
          ref="picker"
          :locale-data="{
            'firstDay': 1,
            'format': 'DD-MM-YYYY',
            'daysOfWeek': [$t('ui.daysOfWeek.sun'), $t('ui.daysOfWeek.mon'), $t('ui.daysOfWeek.tue'), $t('ui.daysOfWeek.wed'), $t('ui.daysOfWeek.thur'), $t('ui.daysOfWeek.fri'), $t('ui.daysOfWeek.sat')],
            'monthNames': [$t('ui.monthNames.jan'), $t('ui.monthNames.feb'), $t('ui.monthNames.mar'), $t('ui.monthNames.apr'), $t('ui.monthNames.may'), $t('ui.monthNames.jun'), $t('ui.monthNames.jul'), $t('ui.monthNames.aug'), $t('ui.monthNames.sep'), $t('ui.monthNames.oct'), $t('ui.monthNames.nov'), $t('ui.monthNames.dec')],
          }"
          class="ml-2"
          :singleDatePicker="false"
          :timePicker="false"
          :showWeekNumbers="false"
          :showDropdowns="false"
          :autoApply="true"
          v-model="date_range"
          :ranges="false"
          :linkedCalendars="true">
          <template v-slot:input="picker" style="min-width: 350px;">
            {{ formatDateStart(picker.startDate) }} - {{ formatDateEnd(picker.endDate) }}
          </template>
        </date-range-picker>

        <v-select
          style="width: 300px;"
          class="ml-2"
          :options="paidStatusOptionsForSelect"
          v-model="filters.status"
          :placeholder="$t('ui.isSettled')"
          :searchable="false"
          label="text">
        </v-select>

        <v-select
          style="width: 300px;"
          class="ml-2"
          :options="invoiceSendStatusOptionsForSelect"
          v-model="filters.invoice_sent"
          :placeholder="$t('ui.invoiceIsSentQuestion')"
          :searchable="false"
          label="text">
        </v-select>

        <d-button
          theme="info"
          outline
          :class="{ 'text-nowrap': true, 'ml-2': !userIsCustomer, 'ml-auto': userIsCustomer }"
          @click.prevent="validateFiltersForm">{{ $t('ui.search') }}</d-button>

        <d-button
          theme="danger"
          outline
          class="text-nowrap ml-2"
          @click.prevent="clearFilters">{{ $t('ui.resetFilters') }}</d-button>
      </validation-observer>
    </d-row>

    <d-card style="width: 100%;">
      <table class="table mt-0 mb-0 full-width">
        <thead class="bg-light">
          <tr>
            <th scope="col" class="border-0" :style="!userIsCustomer ? '' : 'width: 160px;'">{{ $t('ui.invoiceNumber') }}</th>
            <th scope="col" class="border-0" v-if="!userIsCustomer">{{ $t('users.customer') }}</th>
            <th scope="col" class="border-0" style="width: 120px;">{{ $t('settlements.type') }}</th>
            <th scope="col" class="border-0" style="width: 150px;">{{ $t('settlements.amount') }}</th>
            <th scope="col" class="border-0" style="width: 180px;">{{ $t('settlements.deadlines') }}</th>
            <th scope="col" class="border-0" style="width: 160px;">{{ $t('status.status') }}</th>
            <th scope="col" class="border-0" style="width: 180px;">{{ $t('settlements.submitStatus') }}</th>
            <th scope="col" class="border-0" style="width: 240px;">{{ $t('ui.actions') }}</th>
          </tr>
        </thead>
        <tbody v-if="loaded">
          <tr
            v-for="(settlement, index) of settlements"
            :key="'settlement-' + index">
            <td class="align-middle">
              {{ settlement.number }}<br>
              <small v-if="userIsAdmin">
                {{ $t('settlements.reminders.status') }}:
                <span
                  v-if="settlement.exclude_from_invoice_reminder"
                  class="text-danger">
                  {{ $t('settlements.reminders.inactive') }}
                </span>
                <span
                  v-else
                  class="text-success">
                  {{ $t('settlements.reminders.active') }}
                </span>
                <a
                  v-if="settlement.exclude_from_invoice_reminder && !remindersChangeInProgress"
                  href="#"
                  class="text-primary"
                  @click.prevent="invoiceRemindersToggle('off', settlement.id)">
                  ({{ $t('settlements.reminders.enable') }})
                </a>
                <a
                  v-else-if="!remindersChangeInProgress"
                  class="text-primary"
                  href="#"
                  @click.prevent="invoiceRemindersToggle('on', settlement.id)">
                  ({{ $t('settlements.reminders.disable') }})
                </a>
                <span v-else-if="remindersChangeInProgress === settlement.id">
                  <span class="spinner-border spinner-border-sm" role="status"></span>
                </span>
              </small>
            </td>
            <td
              v-if="!userIsCustomer"
              class="align-middle">
              {{ settlement.customer.name }}
            </td>
            <td class="align-middle">
              {{ settlement.type }}
            </td>
            <td class="align-middle">
              {{ Number(settlement.amount).toFixed(2) }} {{ settlement.currency }}
            </td>
            <td class="align-middle">
              <small>
                {{ $t('settlements.issued') }}: <strong>{{ settlement.invoice_date }}</strong><br>
                {{ $t('settlements.paymentDueDate') }}: <strong>{{ settlement.payment_date }}</strong><br>
                {{ $t('ui.settled') }}: <strong>{{ settlement.settlement_date }}</strong>
              </small>
            </td>
            <td class="align-middle">
              <span :class="paidStatusOptions[settlement.status].cssClass">
                {{ paidStatusOptions[settlement.status].label }}
              </span>

              <template v-if="userIsAdmin">
                <br>
                <small
                  v-if="settlement.status === 'PAID'"
                  :style="statusChangeInProgress ? 'pointer-events: none; opacity: .25;' : ''">
                  <a
                    href="#"
                    @click.prevent="changePaymentStatus(settlement.id, 'UNPAID')">
                    <template v-if="statusChangeInProgress !== settlement.id">
                      {{ $t('settlements.changeToUnpaid') }}
                    </template>
                    <div v-else class="spinner-border spinner-border-sm" role="status"></div>
                  </a>
                </small>
                <small
                  v-else-if="settlement.status === 'UNPAID'"
                  :style="statusChangeInProgress ? 'pointer-events: none; opacity: .25;' : ''">
                  <a
                    href="#"
                    @click.prevent="changePaymentStatus(settlement.id, 'PAID')">
                    <template v-if="statusChangeInProgress !== settlement.id">
                      {{ $t('settlements.changeToPaid') }}
                    </template>
                    <div v-else class="spinner-border spinner-border-sm" role="status"></div>
                  </a>
                </small>
              </template>
            </td>
            <td class="align-middle">
              <span
                v-if="settlement.invoice_sent"
                class="text-success">
                {{ $t('settlements.invoiceSent') }}
              </span>
              <d-button
                theme="info"
                v-else-if="!settlement.invoice_sent && settlement.can_send_invoice"
                :disabled="!!sendInvoiceInProgress"
                @click.prevent="sendInvoice(settlement.id)">
                <template v-if="sendInvoiceInProgress !== settlement.id">
                  <i class="material-icons">cloud_upload</i>
                  {{ $t('settlements.sendInvoice') }}
                </template>
                <template v-else>
                  <div class="spinner-border spinner-border-sm" role="status"></div>
                </template>
              </d-button>
              <small
                v-else
                class="text-danger">
                {{ $t('settlements.invoiceUnableToSent') }}
              </small>
            </td>
            <td class="align-middle">
              <i
                v-if="!settlement.file || !settlement.file_url"
                class="material-icons"
                :title="$t('settlements.waitingForInvoice')"
                style="cursor: help">
                schedule
              </i>

              <d-button
                v-else
                theme="info"
                :disabled="!!downloading"
                @click.prevent="download(settlement.id, settlement.number, settlement.customer.name, settlement.order.order_name)">
                <template v-if="downloading !== settlement.id"><i class="material-icons">cloud_download</i></template>
                <template v-else>
                  <div class="spinner-border spinner-border-sm" role="status"></div>
                </template>
              </d-button>

              <d-button
                v-if="settlement.can_delete_invoice || userIsAdmin"
                theme="danger"
                :disabled="!!deleting || settlement.is_correction"
                @click.prevent="deleteInvoice(settlement.id)"
                class="ml-2"
                :title="$t('settlements.deleteInvoiceRequest')">
                <template v-if="deleting !== settlement.id"><i class="material-icons">delete</i></template>
                <template v-else>
                  <div class="spinner-border spinner-border-sm" role="status"></div>
                </template>
              </d-button>

              <d-button
                v-if="settlement.can_generate_printout"
                theme="warning"
                :disabled="printingout"
                @click.prevent="printoutInvoice(settlement.id)"
                class="ml-2"
                :title="$t('settlements.invoicePrintout')">
                <template v-if="printingout !== settlement.id"><i class="material-icons">print</i></template>
                <template v-else>
                  <div class="spinner-border spinner-border-sm" role="status"></div>
                </template>
              </d-button>

              <d-button
                v-if="userIsAdmin"
                theme="secondary"
                class="ml-2"
                @click.prevent="loadHistory(settlement.id)"
                :title="$t('settlements.viewHistory')">
                <i class="material-icons">info</i>
              </d-button>
            </td>
          </tr>
          <tr v-if="settlements.length === 0">
            <td
              colspan="10"
              class="text-center">
              {{ $t('settlements.noSettlementsToDisplay') }}&hellip;
            </td>
          </tr>
        </tbody>
      </table>

      <div v-if="!loaded" class="pb-3 pt-3 text-center">
        {{ $t('ui.dataLoading') }}&hellip;
      </div>

      <d-alert
        v-if="loadError"
        show
        theme="warning">
        {{ $t('ui.dataLoadingFailed') }} <a href="javascript:window.location.reload();" class="alert-link">{{ $t('ui.refreshPage') }}</a> {{ $t('ui.toTryAgain') }}
      </d-alert>
    </d-card>

    <settlements-history-popup
      v-if="displaySettlementsHistoryPopup"
      :settlementID="selectedSettlement" />

    <settlements-add-popup
      v-if="displaySettlementsAddPopup" />

    <pagination
      :meta-data="paginationMetaData"
      :initial-page="currentPage" />
  </d-container>
</template>

<script>
import CustomerPanel from './../mixins/CustomerPanel.vue';
import debounce from 'lodash.debounce';
import FormUtils from './../utils/FormUtils.js';
import HasFilters from './../mixins/HasFilters.vue';
import HasPagination from './../mixins/HasPagination.vue';
import ListUtils from './../utils/ListUtils.js';
import Pagination from './../components/common/Pagination.vue';
import SelectCustomer from './../components/common/SelectCustomer.vue';
import SettlementsAddPopup from './../components/settlements/SettlementsAddPopup.vue';
import SettlementsHistoryPopup from './../components/settlements/SettlementsHistoryPopup.vue';
import Vue from 'vue';
import DateRangePicker from 'vue2-daterange-picker';
import 'vue2-daterange-picker/dist/vue2-daterange-picker.css';

export default {
  name: 'settlements',
  mixins: [
    CustomerPanel,
    HasFilters,
    HasPagination
  ],
  components: {
    'date-range-picker': DateRangePicker,
    'pagination': Pagination,
    'select-customer': SelectCustomer,
    'settlements-add-popup': SettlementsAddPopup,
    'settlements-history-popup': SettlementsHistoryPopup
  },
  computed: {
    paidStatusOptions () {
      return {
        'PAID': {
          label: this.$t('ui.settled'),
          cssClass: 'text-success'
        },
        'UNPAID': {
          label: this.$t('ui.notSettled'),
          cssClass: 'text-danger'
        }
      };
    },
    paidStatusOptionsForSelect () {
      return [
        {
          text: this.$t('ui.settled'),
          value: 'PAID'
        },
        {
          text: this.$t('ui.notSettled'),
          value: 'UNPAID'
        }
      ];
    },
    invoiceSendStatusOptionsForSelect () {
      return [
        {
          text: this.$t('ui.invoiceIsSent'),
          value: '1'
        },
        {
          text: this.$t('ui.invoiceNotSent'),
          value: '0'
        },
        {
          text: this.$t('ui.invoiceCannotSent'),
          value: 'cant_send'
        }
      ];
    },
    userIsAdmin () {
      return this.$store.getters.getUserType === 'admin';
    }
  },
  data() {
    return {
      settlements: [],
      loaded: false,
      loadError: false,
      deleting: false,
      displaySettlementsHistoryPopup: false,
      downloading: false,
      downloadingAllInvoices: false,
      filters: {
        customer_id: null,
        status: null,
        invoice_sent: null
      },
      date_range: {
        startDate: null,
        endDate: null
      },
      printingout: false,
      sendInvoiceInProgress: false,
      selectedSettlement: false,
      statusChangeInProgress: false,
      displaySettlementsAddPopup: false,
      remindersChangeInProgress: false,
      hasActiveFilters: false
    };
  },
  mounted () {
    this.loadFilteredData();
    this.$bus.$on('settlements-reload-needed', this.debouncedLoadFilteredData);
    this.$bus.$on('ui-select-customer-updated', this.setCustomerID);
    this.$bus.$on('close-settlement-history-popup', this.closeHistory);
    this.$bus.$on('close-settlement-add-popup', this.closeAddPopup);
  },
  methods: {
    debouncedLoadFilteredData: debounce(function () {
      this.$bus.$emit('view-filters-save');
      this.loadFilteredData();
    }, 250),
    loadData () {
      ListUtils.loadPaginatedItemsData(this, {
        endpoint: this.getApiEndpoint('/settlements/items?page=' + this.currentPage),
        listField: 'shippers',
        params: {
          width: ['customer']
        }
      });
    },
    loadFilteredData () {
      let whereData = {};

      if (this.filters.customer_id !== null && this.filters.customer_id > 0) {
        whereData.customer_id = this.filters.customer_id;
      }

      if (this.date_range.startDate !== null) {
        whereData.invoice_date_from = this.prepareDateString(this.date_range.startDate);
      }

      if (this.date_range.endDate !== null) {
        whereData.invoice_date_to = this.prepareDateString(this.date_range.endDate);
      }

      if (this.filters.status !== null) {
        whereData.status = this.filters.status.value;
      }

      if (this.filters.invoice_sent !== null && typeof this.filters.invoice_sent !== 'undefined') {
        whereData.invoice_sent = this.filters.invoice_sent.value;
      }

      if (
        this.filters.customer_id !== null ||
        this.filters.status !== null ||
        this.filters.invoice_sent !== null ||
        this.date_range.startDate !== null ||
        this.date_range.endDate !== null
      ) {
        this.hasActiveFilters = true;
      } else {
        this.hasActiveFilters = false;
      }

      ListUtils.loadPaginatedItemsData(this, {
        endpoint: this.getApiEndpoint('/settlements/items?page=' + this.currentPage),
        listField: 'settlements',
        loadError: 'loadError',
        params: {
          with: ['customer', 'order'],
          where: whereData
        }
      });
    },
    loadHistory (settlementID) {
      this.selectedSettlement = settlementID;
      this.displaySettlementsHistoryPopup = true;
    },
    closeHistory () {
      this.selectedSettlement = false;
      this.displaySettlementsHistoryPopup = false;
    },
    clearFilters () {
      this.loaded = false;
      this.loadError = false;
      this.filters = {
        customer_id: null,
        invoice_date_from: null,
        invoice_date_to: null,
        status: null,
        invoice_sent: null
      };
      this.date_range = {
        startDate: null,
        endDate: null
      };

      this.$bus.$emit('ui-select-customer-reset');
      this.$bus.$emit('view-filters-reset');
      this.$bus.$emit('pagination-reset', false);
    },
    validateFiltersForm () {
      this.$bus.$emit('pagination-reset');
      FormUtils.validate(this.$refs['settlements-filters-form'], this.debouncedLoadFilteredData);
    },
    // eslint-disable-next-line
    download (settlementID, invoiceNumber, customerName, orderName) {
      this.downloading = settlementID;

      this.$http.post(this.getApiEndpoint('/settlements/download-file/' + settlementID), {}, {
        responseType: 'blob'
      }).then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        let fileName = response.request.getResponseHeader('content-disposition').split('filename=')[1].split(';')[0];
        fileName = fileName.replace(/[\s\/\\]/gmi, '-', fileName);
        link.setAttribute('download', fileName.replace(/"/gmi, ''));
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        this.downloading = false;
      }).catch(error => {
        console.log(error);
        Vue.swal({
          title: this.$t('ui.errorOccuredDuringDownload'),
          html: this.$t('ui.tryAgain'),
          type: 'warning',
          confirmButtonText: this.$t('ui.ok'),
          buttonsStyling: true
        });

        this.downloading = false;
      });
    },
    deleteInvoice (settlementID) {
      this.deleting = settlementID;

      this.$http.post(this.getApiEndpoint('/settlements/delete/' + settlementID)).then(response => {
        if (response.data.success) {
          Vue.swal({
            title: this.$t('settlements.invoiceRollbackTitle'),
            html: this.$t('settlements.invoiceRollbackDesc'),
            type: 'success',
            confirmButtonText: this.$t('ui.ok'),
            buttonsStyling: true
          });

          this.loadFilteredData();
        } else {
          Vue.swal({
            title: this.$t('settlements.errorOccuredDuringRollback'),
            html: this.$t('ui.tryAgain'),
            type: 'warning',
            confirmButtonText: this.$t('ui.ok'),
            buttonsStyling: true
          });
        }

        this.deleting = false;
      }).catch(error => {
        console.log(error);
        Vue.swal({
          title: this.$t('ui.errorOccuredDuringSending'),
          html: this.$t('ui.tryAgain'),
          type: 'warning',
          confirmButtonText: this.$t('ui.ok'),
          buttonsStyling: true
        });

        this.deleting = false;
      });
    },
    printoutInvoice (settlementID) {
      this.printingout = settlementID;

      this.$http.post(this.getApiEndpoint('/settlements/generate-printout/' + settlementID)).then(response => {
        if (response.data.success) {
          Vue.swal({
            title: this.$t('settlements.invoicePrintoutTitle'),
            html: this.$t('settlements.invoicePrintoutDesc'),
            type: 'success',
            confirmButtonText: this.$t('ui.ok'),
            buttonsStyling: true
          });

          this.loadFilteredData();
        } else {
          Vue.swal({
            title: this.$t('settlements.errorOccuredDuringPrintout'),
            html: this.$t('ui.tryAgain'),
            type: 'warning',
            confirmButtonText: this.$t('ui.ok'),
            buttonsStyling: true
          });
        }

        this.printingout = false;
      }).catch(error => {
        console.log(error);
        Vue.swal({
          title: this.$t('ui.errorOccuredDuringSending'),
          html: this.$t('ui.tryAgain'),
          type: 'warning',
          confirmButtonText: this.$t('ui.ok'),
          buttonsStyling: true
        });

        this.printingout = false;
      });
    },
    sendInvoice (settlementID) {
      this.sendInvoiceInProgress = settlementID;

      this.$http.post(this.getApiEndpoint('/settlements/send-invoice/' + settlementID)).then(response => {
        if (response.data.success) {
          Vue.swal({
            title: this.$t('settlements.invoiceSentTitle'),
            html: this.$t('settlements.invoiceSentDesc'),
            type: 'success',
            confirmButtonText: this.$t('ui.ok'),
            buttonsStyling: true
          });

          this.loadFilteredData();
        } else {
          Vue.swal({
            title: this.$t('settlements.errorOccuredDuringSending'),
            html: this.$t('ui.tryAgain'),
            type: 'warning',
            confirmButtonText: this.$t('ui.ok'),
            buttonsStyling: true
          });
        }

        this.sendInvoiceInProgress = false;
      }).catch(error => {
        console.log(error);
        Vue.swal({
          title: this.$t('ui.errorOccuredDuringSending'),
          html: this.$t('ui.tryAgain'),
          type: 'warning',
          confirmButtonText: this.$t('ui.ok'),
          buttonsStyling: true
        });
        this.sendInvoiceInProgress = false;
      });
    },
    downloadAllInvoices () {
      this.downloadingAllInvoices = true;
      let today = new Date();
      let todaysDate = [today.getFullYear(), today.getMonth() + 1, today.getDate()].map(n => n < 10 ? '0' + n : n).join('-');
      let whereData = {};

      if (this.hasActiveFilters) {
        if (this.date_range.startDate !== null) {
          whereData.invoice_date_from = this.prepareDateString(this.date_range.startDate);
        }

        if (this.date_range.endDate !== null) {
          whereData.invoice_date_to = this.prepareDateString(this.date_range.endDate);
        }

        if (this.filters.customer_id !== null && this.filters.customer_id > 0) {
          whereData.customer_id = this.filters.customer_id;
        }

        if (this.filters.status !== null) {
          whereData.status = this.filters.status.value;
        }

        if (this.filters.invoice_sent !== null && typeof this.filters.invoice_sent !== 'undefined') {
          whereData.invoice_sent = this.filters.invoice_sent.value;
        }
      } else {
        whereData = {
          invoice_date_from: todaysDate,
          invoice_date_to: todaysDate
        };
      }

      this.$http.post(this.getApiEndpoint('/settlements/download-zipped-files'), {
        where: whereData
      }, {
        responseType: 'blob'
      }).then(response => {
        if (response.success === 0) {
          Vue.swal({
            title: this.$t('ui.errorOccuredDuringDownload'),
            html: this.$t('ui.tryAgain'),
            type: 'warning',
            confirmButtonText: this.$t('ui.ok'),
            buttonsStyling: true
          });
          this.downloadingAllInvoices = false;
          return;
        }

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        let dateRange = '';

        if (whereData.invoice_date_from && whereData.invoice_date_to) {
          dateRange = '-' + whereData.invoice_date_from + '_' + whereData.invoice_date_to;

          if (whereData.invoice_date_from === whereData.invoice_date_to) {
            dateRange = '-' + whereData.invoice_date_from;
          }
        }

        link.setAttribute('download', this.$t('purchase.invoiceFileNamePrefix') + dateRange + '.zip');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        this.downloadingAllInvoices = false;
      }).catch(error => {
        console.log(error);
        Vue.swal({
          title: this.$t('ui.errorOccuredDuringDownload'),
          html: this.$t('ui.tryAgain'),
          type: 'warning',
          confirmButtonText: this.$t('ui.ok'),
          buttonsStyling: true
        });
        this.downloadingAllInvoices = false;
      });
    },
    setCustomerID (customerID) {
      this.filters.customer_id = customerID;
    },
    prepareDateString (input) {
      return [input.getFullYear(), input.getMonth() + 1, input.getDate()].map(n => n < 10 ? '0' + n : n).join('-');
    },
    formatDateStart (input) {
      if (input === null) {
        return this.$t('ui.searchFrom');
      }

      return [input.getFullYear(), input.getMonth() + 1, input.getDate()].map(n => n < 10 ? '0' + n : n).join('-');
    },
    formatDateEnd (input) {
      if (input === null) {
        return this.$t('ui.searchTo');
      }

      return [input.getFullYear(), input.getMonth() + 1, input.getDate()].map(n => n < 10 ? '0' + n : n).join('-');
    },
    changePaymentStatus (settlementID, newStatus) {
      this.statusChangeInProgress = settlementID;

      this.$http.post(this.getApiEndpoint('/settlements/change-status/' + settlementID), {
        status: newStatus
      }).then(response => {
        if (response.data.success) {
          Vue.swal({
            title: this.$t('settlements.statusChangedTitle'),
            html: this.$t('settlements.statusChangedDesc'),
            type: 'success',
            confirmButtonText: this.$t('ui.ok'),
            buttonsStyling: true
          });

          this.loadFilteredData();
        } else {
          Vue.swal({
            title: this.$t('ui.errorOccured'),
            html: this.$t('ui.errorOccuredDesc'),
            type: 'warning',
            confirmButtonText: this.$t('ui.ok'),
            buttonsStyling: true
          });
        }

        this.statusChangeInProgress = false;
      }).catch(error => {
        console.log(error);
        Vue.swal({
          title: this.$t('ui.errorOccured'),
          html: this.$t('ui.errorOccuredDesc'),
          type: 'warning',
          confirmButtonText: this.$t('ui.ok'),
          buttonsStyling: true
        });

        this.statusChangeInProgress = false;
      });
    },
    addSettlement () {
      this.displaySettlementsAddPopup = true;
    },
    closeAddPopup () {
      this.displaySettlementsAddPopup = false;
    },
    invoiceRemindersToggle (newState, settlementID) {
      this.remindersChangeInProgress = settlementID;

      FormUtils.submitInBackground({
        $http: this.$http,
        endpoint: '/settlements/update-exclude-from-invoice-reminder/' + settlementID,
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('ui.actionFailedTryAgain'),
        formData: {
          exclude_from_invoice_reminder: newState === 'on' ? 1 : 0
        },
        successAction: () => {
          this.remindersChangeInProgress = false;
          this.$bus.$emit('settlements-reload-needed');
        },
        failAction: () => {
          this.remindersChangeInProgress = false;
        }
      });
    }
  },
  beforeDestroy () {
    this.$bus.$off('settlements-reload-needed', this.debouncedLoadFilteredData);
    this.$bus.$off('ui-select-customer-updated', this.setCustomerID);
    this.$bus.$off('close-settlement-history-popup', this.closeHistory);
    this.$bus.$off('close-settlement-add-popup', this.closeAddPopup);
  }
};
</script>

